import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminRoute } from "./components/AdminRoute";
import { lazy, Suspense } from "react";
import { Skeleton } from "@/components/ui/skeleton";

// Lazy load pages for better performance
const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const Cadastro = lazy(() => import("./pages/Cadastro"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CriarLoja = lazy(() => import("./pages/CriarLoja"));
const Produtos = lazy(() => import("./pages/Produtos"));
const Pedidos = lazy(() => import("./pages/Pedidos"));
const Cupons = lazy(() => import("./pages/Cupons"));
const Relatorios = lazy(() => import("./pages/Relatorios"));
const Configuracoes = lazy(() => import("./pages/ConfiguracoesLoja"));
const Loja = lazy(() => import("./pages/Loja"));
const Checkout = lazy(() => import("./pages/Checkout"));
const PedidoConfirmado = lazy(() => import("./pages/PedidoConfirmado"));
const VerificarPagamento = lazy(() => import("./pages/VerificarPagamento"));
const DiagnosticoPagamentos = lazy(() => import("./pages/DiagnosticoPagamentos"));
const MeusPedidos = lazy(() => import("./pages/MeusPedidos"));
const Pagamentos = lazy(() => import("./pages/Pagamentos"));
const Compartilhar = lazy(() => import("./pages/Compartilhar"));
const Planos = lazy(() => import("./pages/Planos"));
const Clientes = lazy(() => import("./pages/Clientes"));
const ContaSeguranca = lazy(() => import("./pages/ContaSeguranca"));
const CentralAjuda = lazy(() => import("./pages/CentralAjuda"));
const ComoFunciona = lazy(() => import("./pages/ComoFunciona"));
const Comparativo = lazy(() => import("./pages/Comparativo"));
const Lojas = lazy(() => import("./pages/Lojas"));
const Ajuda = lazy(() => import("./pages/Ajuda"));
const Contato = lazy(() => import("./pages/Contato"));
const Privacidade = lazy(() => import("./pages/Privacidade"));
const Termos = lazy(() => import("./pages/Termos"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ConfiguracoesPagamentos = lazy(() => import("./pages/ConfiguracoesPagamentos"));
const SuccessSubscription = lazy(() => import("./pages/SuccessSubscription"));
const AdminPagamentos = lazy(() => import("./pages/AdminPagamentos"));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="space-y-4 w-full max-w-md px-4">
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-64 w-full" />
      <Skeleton className="h-12 w-full" />
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      gcTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/cadastro" element={<Cadastro />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/criar-loja" element={<ProtectedRoute><CriarLoja /></ProtectedRoute>} />
              <Route path="/produtos" element={<ProtectedRoute><Produtos /></ProtectedRoute>} />
              <Route path="/pedidos" element={<ProtectedRoute><Pedidos /></ProtectedRoute>} />
              <Route path="/cupons" element={<ProtectedRoute><Cupons /></ProtectedRoute>} />
              <Route path="/configuracoes-pagamentos" element={<ProtectedRoute><ConfiguracoesPagamentos /></ProtectedRoute>} />
              <Route path="/relatorios" element={<ProtectedRoute><Relatorios /></ProtectedRoute>} />
              <Route path="/configuracoes" element={<ProtectedRoute><Configuracoes /></ProtectedRoute>} />
              <Route path="/meus-pedidos" element={<ProtectedRoute><MeusPedidos /></ProtectedRoute>} />
              <Route path="/pagamentos" element={<ProtectedRoute><Pagamentos /></ProtectedRoute>} />
              <Route path="/compartilhar" element={<ProtectedRoute><Compartilhar /></ProtectedRoute>} />
              <Route path="/planos" element={<ProtectedRoute><Planos /></ProtectedRoute>} />
              <Route path="/success-subscription" element={<ProtectedRoute><SuccessSubscription /></ProtectedRoute>} />
              <Route path="/clientes" element={<ProtectedRoute><Clientes /></ProtectedRoute>} />
              <Route path="/conta-seguranca" element={<ProtectedRoute><ContaSeguranca /></ProtectedRoute>} />
              <Route path="/central-ajuda" element={<CentralAjuda />} />
              <Route path="/como-funciona" element={<ComoFunciona />} />
              <Route path="/comparativo" element={<Comparativo />} />
              <Route path="/lojas" element={<Lojas />} />
              <Route path="/ajuda" element={<Ajuda />} />
              <Route path="/contato" element={<Contato />} />
              <Route path="/privacidade" element={<Privacidade />} />
              <Route path="/termos" element={<Termos />} />
              <Route path="/loja/:slug" element={<Loja />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/pedido-confirmado" element={<PedidoConfirmado />} />
              <Route path="/verificar-pagamento" element={<VerificarPagamento />} />
              <Route path="/diagnostico-pagamentos" element={<DiagnosticoPagamentos />} />
              <Route path="/admin/pagamentos" element={<AdminRoute><AdminPagamentos /></AdminRoute>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
